/**
 * Pure CSV utility functions for chart data exports.
 * Follows RFC 4180 for field escaping with formula injection protection.
 */
import type { OutputStatistics } from './atoms';

const FORMULA_TRIGGERS = /^[=+\-@\t\r]/;

/** Escape a single CSV field per RFC 4180, with formula injection protection. */
export function csvEscapeField(value: unknown): string {
  if (value == null) return '';
  const str = String(value);
  const safe = FORMULA_TRIGGERS.test(str) ? "'" + str : str;
  if (safe.includes(',') || safe.includes('"') || safe.includes('\n') || safe.includes('\r')) {
    return '"' + safe.replace(/"/g, '""') + '"';
  }
  return safe;
}

/** Convert a timestamp value to ISO 8601 string for CSV output. */
export function formatTimestampForCsv(value: unknown): string {
  if (value == null) return '';
  const num = Number(value);
  if (!Number.isNaN(num) && num > 0) {
    return new Date(num).toISOString();
  }
  const str = String(value);
  const parsed = Date.parse(str);
  if (!Number.isNaN(parsed)) {
    return new Date(parsed).toISOString();
  }
  return str;
}

/**
 * Build complete CSV content for county chart data.
 *
 * @param data - Array of data point objects from the chart dataset (each has lastupdatedon, county, and stat fields)
 * @param outputStats - Output statistics config describing which fields to include as columns
 * @param labelsObject - Mapping from stat field names to human-readable labels
 * @returns CSV string ready for download (no data URI prefix)
 */
export function buildCountyCsvContent(
  data: Record<string, unknown>[],
  outputStats: Pick<OutputStatistics, 'onStatisticField'>[],
  labelsObject: Record<string, string>
): string {
  const statFields = outputStats.map(v => v.onStatisticField);
  const headerLabels = statFields.map(f => labelsObject[f] ?? f);

  const headRow = ['Model Time', 'County', ...headerLabels].map(csvEscapeField).join(',');

  if (!data || data.length === 0) return headRow;

  const rows = data.map(row => {
    const timestamp = formatTimestampForCsv(row.lastupdatedon);
    const county = csvEscapeField(row.county ?? row.counties_name ?? row.name ?? row.x ?? '');
    const stats = statFields.map(f => csvEscapeField(row[f]));
    return [csvEscapeField(timestamp), county, ...stats].join(',');
  });

  return [headRow, ...rows].join('\n');
}

/**
 * Build complete CSV content for time series chart data.
 *
 * @param datasets - Chart datasets array, each with label, key, and data points
 * @param defaultLabel - The label of the dataset to export
 * @param selectedModels - Array of selected model time strings (epoch strings) to filter by
 * @returns CSV string ready for download, or empty string if no matching data
 */
export function buildTimeSeriesCsvContent(
  datasets: { label: string; data: Record<string, unknown>[] }[],
  defaultLabel: string,
  selectedModels: string[]
): string {
  const matching = datasets.filter(d => d.label === defaultLabel);
  if (matching.length === 0 || !matching[0].data || matching[0].data.length === 0) return '';

  const dataset = matching[0];
  const headRow = ['Model', 'Model Time', csvEscapeField(dataset.label)].join(',');

  const rows = dataset.data
    .filter(d => selectedModels.includes(String(d.x)))
    .map(d => {
      const comment = csvEscapeField(d.comments ?? '');
      const timestamp = formatTimestampForCsv(d.x);
      const value = csvEscapeField(d.y);
      return [comment, csvEscapeField(timestamp), value].join(',');
    });

  if (rows.length === 0) return '';

  return [headRow, ...rows].join('\n');
}
